export type ServitutAktSearchServitut = {
   dokumentFilnavn?: string | null;
   tekster?: Array<string | { tekst?: string | null } | null> | null;
   tekstSummarisk?: string | null;
};

export type ServitutAktSearchTargets = {
   /** Akt the servitut points at (`dokumentFilnavn`). */
   servitutAktId: string | null;
   /** Akt id parsed from tingbog one-line text, e.g. `36_Æ_95`. */
   textAktId: string | null;
   /** Property-level øvrige akt. */
   ovrigAktId: string | null;
};

/**
 * Akt ids in tingbog one-liners look like `36_Æ_95` or `36_Ø_430`:
 * three token parts separated by two underscores, somewhere in the text.
 */
const AKT_ID_IN_TEXT_RE = /(?<![A-Za-z0-9ÆØÅæøå])(\d+[A-Za-zÆØÅæøå]*_[A-Za-zÆØÅæøå0-9]+_\d+)(?![A-Za-z0-9ÆØÅæøå])/gu;

function trimId(value?: string | null): string | null {
   const trimmed = value?.trim();
   return trimmed ? trimmed : null;
}

function textFromTekstEntry(entry: unknown): string {
   if (typeof entry === "string") {
      return entry;
   }
   if (entry && typeof entry === "object" && "tekst" in entry) {
      const tekst = (entry as { tekst?: unknown }).tekst;
      return typeof tekst === "string" ? tekst : "";
   }
   return "";
}

export function collectServitutSearchText(servitut?: ServitutAktSearchServitut | null): string {
   if (!servitut) {
      return "";
   }
   const parts: string[] = [];
   if (Array.isArray(servitut.tekster)) {
      for (const entry of servitut.tekster) {
         const text = textFromTekstEntry(entry).trim();
         if (text) {
            parts.push(text);
         }
      }
   }
   const summary = servitut.tekstSummarisk?.trim();
   if (summary) {
      parts.push(summary);
   }
   return parts.join("\n");
}

export function extractAktIdsFromServitutText(servitut?: ServitutAktSearchServitut | null): string[] {
   const text = collectServitutSearchText(servitut);
   if (!text) {
      return [];
   }
   const found: string[] = [];
   const seen = new Set<string>();
   for (const match of text.matchAll(AKT_ID_IN_TEXT_RE)) {
      const value = match[1]?.trim();
      if (!value || seen.has(value)) {
         continue;
      }
      seen.add(value);
      found.push(value);
   }
   return found;
}

export function getServitutAktSearchTargets(
   servitut?: ServitutAktSearchServitut | null,
   stamOplysninger?: { indskannetAktDokumentId?: string | null } | null,
): ServitutAktSearchTargets {
   const servitutAktId = trimId(servitut?.dokumentFilnavn);
   const ovrigAktId = trimId(stamOplysninger?.indskannetAktDokumentId);
   const textAktId = extractAktIdsFromServitutText(servitut).find((aktId) => aktId !== servitutAktId) || null;
   return {
      servitutAktId,
      textAktId,
      ovrigAktId,
   };
}

/** Pointed akt, then regex akt in the servitut text, then øvrige akt. */
export function getServitutPdfFetchAktId(
   servitut?: ServitutAktSearchServitut | null,
   stamOplysninger?: { indskannetAktDokumentId?: string | null } | null,
): string | null {
   const { servitutAktId, textAktId, ovrigAktId } = getServitutAktSearchTargets(servitut, stamOplysninger);
   return servitutAktId || textAktId || ovrigAktId || null;
}

export function isServitutFromOvrigAktSource(
   servitut?: ServitutAktSearchServitut | null,
   stamOplysninger?: { indskannetAktDokumentId?: string | null } | null,
): boolean {
   const { servitutAktId, textAktId, ovrigAktId } = getServitutAktSearchTargets(servitut, stamOplysninger);
   return !servitutAktId && !textAktId && Boolean(ovrigAktId);
}

export function isServitutFromTextAktSource(
   servitut?: ServitutAktSearchServitut | null,
   stamOplysninger?: { indskannetAktDokumentId?: string | null } | null,
): boolean {
   const { servitutAktId, textAktId } = getServitutAktSearchTargets(servitut, stamOplysninger);
   return !servitutAktId && Boolean(textAktId);
}
